import { Component, Inject, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';
import { CustomerEntity } from '../../../../dave-data-module/entities/customer.entity';
import { ContractEntity } from '../../../../dave-data-module/entities/mdqx/contract.entity';
import { State } from '../../../../dave-data-module/State';
import {
    DeleteContractParticipantRequest,
    PostContractParticipantRequest,
} from '../../../../dave-data-module/State/actions/mdqx/contract-participant.actions';
import { getCustomers } from '../../../../dave-data-module/State/selectors/customers.selectors';
import { getContractParticipants, getContracts } from '../../../../dave-data-module/State/selectors/mdqx/selectors';
import {
    InfoDialogComponent,
    InfoDialogData,
} from '../../../../dave-utils-module/dave-shared-components-module/components/dialogs/info-dialog/info-dialog.component';
import { isNotNullOrUndefined } from '../../../../helper/helper';

interface DisplayContract {
    Selected: boolean;
    Contract: ContractEntity;
}

@Component({
    selector: 'app-select-contract-popup',
    templateUrl: './select-contract-popup.component.html',
    styleUrls: ['./select-contract-popup.component.scss'],
})
export class SelectContractPopupComponent implements OnInit {
    public DisplayContracts$: Observable<DisplayContract[]>;
    protected selectedContracts$: BehaviorSubject<number[]> = new BehaviorSubject<number[]>([]);

    constructor(
        public DialogRef: MatDialogRef<SelectContractPopupComponent>,
        private store: Store<State>,
        @Inject(MAT_DIALOG_DATA) public Data: number,
        private dialog: MatDialog,
    ) {}

    ngOnInit(): void {
        this.DisplayContracts$ = combineLatest([
            this.store
                .select(getContractParticipants)
                .pipe(map(cps => cps.filter(cp => cp.CustomerId === this.Data))),
            this.selectedContracts$,
            this.store.select(getContracts),
        ]).pipe(
            tap(([contractParticipants, selected, contracts]) => {
                if (!contractParticipants || contracts.length < 1) {
                    // es gibt keine Kunden
                    this.dialog
                        .open<InfoDialogComponent, InfoDialogData>(InfoDialogComponent, {
                            data: {
                                heading: 'Keine Verträge Vorhanden',
                                paragraph:
                                    'Es sind keine Verträge vorhanden die ausgewählt werden könnten. Bitte legen sie zuerst welche an. Dafür gehen Sie in die Vertragsverwaltung und legen einen neuen Kunden an.',
                                routingButtonRoute: 'vertraege/neuerVertrag',
                                routingButtonText: 'Hier geht es zur Vertragsverwaltung',
                            },
                            maxWidth: '50rem',
                        })
                        .afterClosed()
                        .pipe(
                            take(1),
                            tap(res => {
                                this.DialogRef.close();
                            }),
                        )
                        .subscribe();
                }
            }),
            map(([contractParticipants, selected, contracts]) =>
                contracts.map(c => {
                    return {
                        Selected: selected.includes(c.Id),
                        Contract: c,
                    };
                }),
            ),
        );
        this.store
            .select(getContractParticipants)
            .pipe(
                take(1),
                map(cps =>
                    cps
                        .filter(cp => cp.CustomerId === this.Data)
                        .map(cp => cp.ContractId),
                ),
                // remove duplicates
                map(ids => ids.filter((value, index) => ids.indexOf(value) === index)),
            )
            .subscribe(ids => this.selectedContracts$.next(ids));
    }

    public ToggleSelected(id: number) {
        if (this.selectedContracts$.getValue().includes(id)) {
            this.selectedContracts$.next(this.selectedContracts$.getValue().filter(i => i !== id));
        } else {
            const value = Object.assign([], this.selectedContracts$.getValue());
            value.push(id);
            this.selectedContracts$.next(value);
        }
    }

    public SelectAll() {
        this.store
            .select(getCustomers)
            .pipe(
                take(1),
                map(cs => cs.map(c => c.Id)),
            )
            .subscribe(ids => this.selectedContracts$.next(ids));
    }

    public Close() {
        this.DialogRef.close();
    }

    public Save() {
        this.store
            .select(getContractParticipants)
            .pipe(
                take(1),
                map(cps => {
                    for (const contractId of this.selectedContracts$.getValue()) {
                        if (!cps.find(cp => cp.ContractId === contractId && cp.CustomerId === this.Data)) {
                            this.store.dispatch(
                                new PostContractParticipantRequest({
                                    contractId,
                                    customerId: this.Data,
                                }),
                            );
                        }
                    }
                    cps.filter(cp => cp.CustomerId === this.Data).map(cp => {
                        if (!isNotNullOrUndefined(this.selectedContracts$.getValue().find(cId => cId === cp.ContractId))) {
                            this.store.dispatch(
                                new DeleteContractParticipantRequest({
                                    id: cp.Id,
                                }),
                            );
                        }
                    });
                }),
            )
            .subscribe();
        this.DialogRef.close();
    }
}
